import type { Facing } from "odyssey-shared";
import type { CharacterFigure, Pose } from "../character.ts";
import { COMPANION_LOOKS } from "./constants.ts";
import { CompanionFollow } from "./follow.ts";

/**
 * The companions drawn on a page (ADR-142 decision 7.2): one figure and one follow for each player in view whose view
 * carries a companion. The page tells it each frame who is in view with a companion (`owner`) and then `finish`es the
 * frame, which poses every companion at the place his follow reached and removes those whose player left the view or
 * sent him home. A companion is never an entity: he has no collision box, is not targetable and is not in the click
 * order, and nothing of him is sent to the server.
 */

/**
 * One companion drawn.
 */
interface Drawn {
  readonly companion: number;
  readonly own: boolean;
  source: string;
  words: string;
  readonly follow: CompanionFollow;
  readonly figure: CharacterFigure;
  seen: boolean;
}

/**
 * Makes a companion's figure.
 *
 * @param sheet The character sheet the manifest holds for him.
 * @param scale The exact scale he is drawn at.
 * @returns The figure, or null when the page holds no such sheet.
 */
export type CompanionFigureMaker = (sheet: string, scale: number) => CharacterFigure | null;

/**
 * Where a companion's name shows while the pointer is over him: one label the page draws in the world.
 */
export interface CompanionLabel {
  /**
   * Shows the label over a point.
   *
   * @param text The words.
   * @param x World pixels across, the middle of the label.
   * @param y World pixels down, the label's foot.
   */
  show(text: string, x: number, y: number): void;
  /** Hides the label. */
  hide(): void;
}

/**
 * How far from his feet the pointer may be across and up, in world pixels, to be over him.
 */
const HOVER_HALF_WIDTH = 10;
const HOVER_HEIGHT = 20;

/**
 * Every companion on the page, by his player's handle.
 */
export class CompanionFollowers {
  private readonly drawn = new Map<number, Drawn>();
  private readonly pose: { x: number; y: number; facing: Facing; moving: boolean; running: boolean } = { x: 0, y: 0, facing: 0, moving: false, running: false };
  private readonly gone: number[] = [];
  private othersShown = true;
  private nowMs = 0;
  private pointerX = Number.NaN;
  private pointerY = Number.NaN;
  private pointed = false;
  private shown = "";

  /**
   * @param make Makes a companion's figure.
   * @param tile World pixels in a tile.
   * @param flyGapTiles The gap in tiles past which a companion takes to the air (the rules' `companion_fly_gap`).
   * @param title The words over a companion while the pointer is on him, from what the page holds of his player (the
   *   companion's own name for the page's own, the player's first name for another's); asked only when that changes.
   * @param label Where his name shows while the pointer is over him.
   * @param show Whether another player's companion is drawn at all, asked once a frame by `begin`: false on low detail
   *   (a reduced render resolution), which hides other players' companions first (ADR-142, "thousands of companions").
   */
  public constructor(
    private readonly make: CompanionFigureMaker,
    private readonly tile: number,
    private readonly flyGapTiles: number,
    private readonly title: (own: boolean, source: string) => string,
    private readonly label: CompanionLabel | null = null,
    private readonly show: () => boolean = () => true,
  ) {}

  /**
   * The companions drawn, by his player's handle, for tests and the report.
   *
   * @returns The count.
   */
  public get count(): number {
    return this.drawn.size;
  }

  /**
   * The words shown over a companion now, empty while the pointer is on none, for tests.
   *
   * @returns The words.
   */
  public get shownTitle(): string {
    return this.shown;
  }

  /**
   * The figure of the companion a player has out, for tests and screenshots.
   *
   * @param handle His player's handle.
   * @returns The figure, or undefined when none is drawn.
   */
  public figureOf(handle: number): CharacterFigure | undefined {
    return this.drawn.get(handle)?.figure;
  }

  /**
   * Where a companion's follow has him, for tests and screenshots.
   *
   * @param handle His player's handle.
   * @returns The follow, or undefined when none is drawn.
   */
  public followOf(handle: number): CompanionFollow | undefined {
    return this.drawn.get(handle)?.follow;
  }

  /**
   * Starts a frame: reads once whether another player's companion is drawn.
   */
  public begin(): void {
    this.othersShown = this.show();
  }

  /**
   * Tells it a player in view this frame and the companion his view carries. A companion first seen starts beside his
   * player; one whose id changed is drawn again.
   *
   * @param handle The player's handle.
   * @param companion The companion's id in the player's view, 0 for none.
   * @param own Whether the player is the page's own.
   * @param x The player's feet across, in world pixels, as drawn.
   * @param y The same down.
   * @param facing The way the player faces.
   * @param runs Whether the player runs.
   * @param deltaMs The frame's length in ms.
   * @param source What the page holds of his player for the words over him: the companion's name for the page's own, the
   *   player's first name for another's.
   */
  public owner(handle: number, companion: number, own: boolean, x: number, y: number, facing: number, runs: boolean, deltaMs: number, source: string): void {
    if (companion === 0 || (!own && !this.othersShown)) {
      return;
    }
    let held = this.drawn.get(handle);
    if (held !== undefined && held.companion !== companion) {
      held.figure.destroy();
      this.drawn.delete(handle);
      held = undefined;
    }
    if (held === undefined) {
      const look = COMPANION_LOOKS.get(companion);
      const figure = look === undefined ? null : this.make(look.sheet, look.scale);
      if (figure === null) {
        return;
      }
      const follow = new CompanionFollow(this.flyGapTiles);
      follow.reset(x, y, facing, this.tile);
      held = { companion, own, source, words: this.title(own, source), follow, figure, seen: true };
      this.drawn.set(handle, held);
    }
    held.seen = true;
    if (held.source !== source) {
      held.source = source;
      held.words = this.title(held.own, source);
    }
    held.follow.step(x, y, facing, runs, deltaMs, this.tile);
  }

  /**
   * Ends a frame: poses the companions told this frame and destroys the rest.
   *
   * @param nowMs Scene time in ms.
   * @param pointerX The pointer's world point across, NaN for none.
   * @param pointerY The same down.
   */
  public finish(nowMs: number, pointerX = Number.NaN, pointerY = Number.NaN): void {
    this.nowMs = nowMs;
    this.pointerX = pointerX;
    this.pointerY = pointerY;
    this.pointed = false;
    this.drawn.forEach(this.finishOne);
    if (!this.pointed) {
      this.shown = "";
      this.label?.hide();
    }
    for (let at = 0; at < this.gone.length; at += 1) {
      const handle = this.gone[at] as number;
      this.drawn.get(handle)?.figure.destroy();
      this.drawn.delete(handle);
    }
    this.gone.length = 0;
  }

  /**
   * Poses one companion for the frame `finish` is ending, or marks it gone when its player was not told; made once, so a
   * frame allocates nothing visiting them.
   *
   * @param held The companion.
   * @param handle His player's handle.
   */
  private readonly finishOne = (held: Drawn, handle: number): void => {
    if (!held.seen) {
      this.gone.push(handle);
      return;
    }
    held.seen = false;
    const pose = this.pose;
    const follow = held.follow;
    pose.x = Math.round(follow.x);
    pose.y = Math.round(follow.y);
    pose.facing = follow.facing as Facing;
    pose.moving = follow.moving;
    pose.running = follow.running;
    held.figure.setPose(pose as Pose, this.nowMs);
    const lift = held.figure.lift;
    if (!this.pointed && Math.abs(this.pointerX - pose.x) <= HOVER_HALF_WIDTH && this.pointerY <= pose.y + 2 && this.pointerY >= pose.y - lift - HOVER_HEIGHT) {
      this.pointed = true;
      this.shown = held.words;
      this.label?.show(held.words, pose.x, pose.y - lift - HOVER_HEIGHT);
    }
  };

  /**
   * Destroys every companion, as a session or a map ends.
   */
  public clear(): void {
    for (const held of this.drawn.values()) {
      held.figure.destroy();
    }
    this.drawn.clear();
    this.label?.hide();
  }
}
